import { DirectPathBookingApplinkParams } from '../searchForCare/constants'

/**
 * Categories for grouping searchable pages.
 * Used for ranking - lower priority number = higher rank in search results.
 */
export enum SEARCH_PAGE_CATEGORY {
  PROGRAM = 'program',
  TRIAGE = 'triage',
  TOOLKIT = 'toolkit',
  ACCOUNT = 'account',
  LIBRARY = 'library',
  CARE = 'care',
  EXTERNAL = 'external',
  HELP = 'help',
}

/**
 * Type identifier for searchable page metadata.
 */
export enum SEARCHABLE_PAGE_TYPE {
  STANDARD = 'standard',
  PARAM_ROUTE = 'paramRoute',
  DIRECT_PROGRAM_LINK = 'directProgramLink',
  EXTERNAL_URL = 'externalUrl',
}

/**
 * Unique identifiers for each searchable page.
 * Used for type-safe references in eligibility checks, analytics, etc.
 */
export enum SEARCHABLE_PAGE_ID {
  // Triage
  ADULT_TRIAGE = 'adult-triage',
  CHILD_TRIAGE = 'child-triage',
  FORKED_TRIAGE = 'forked-triage',

  // Programs (DPLs)
  COACHING = 'coaching',
  PARENTING_COACHING = 'parenting-coaching',
  COUPLES_THERAPY = 'couples-therapy',
  FAMILY_THERAPY = 'family-therapy',
  MEDICATION_CONSULT = 'medication-consult',
  RENEW_AUD = 'renew-aud',

  // Complex Care / External
  COMPLEX_CARE = 'complex-care',
  DBT = 'dbt',
  IOP = 'iop',
  PHP = 'php',
  RESIDENTIAL = 'residential',
  INPATIENT = 'inpatient',

  // Toolkits
  TOOLKIT_RELATIONSHIPS = 'toolkit-relationships',
  TOOLKIT_WORK = 'toolkit-work',
  TOOLKIT_FAMILY = 'toolkit-family',
  TOOLKIT_NEURODIVERSITY = 'toolkit-neurodiversity',
  TOOLKIT_HEALTH_CHALLENGES = 'toolkit-health-challenges',
  TOOLKIT_PARENTING = 'toolkit-parenting',
  TOOLKIT_VALUES = 'toolkit-values',
  TOOLKIT_TRAUMA = 'toolkit-trauma',

  // Care Tab
  CARE_TAB = 'care-tab',
  SESSIONS = 'sessions',

  // Library
  LIBRARY = 'library',
  EVENTS = 'events',
  MEDITATION = 'meditation',

  // Benefits / Wellness
  BENEFITS = 'benefits',
  WELLNESS_CHECK_IN = 'wellness-check-in',

  // Account
  ACCOUNT = 'account',
  SETTINGS = 'settings',
  SESSION_HISTORY = 'session-history',
  COVERAGE_INFO = 'coverage-info',
  HEALTH_PLAN_PAYMENT = 'health-plan-payment',
  PRIVACY_LEGAL = 'privacy-legal',
  CLINICAL_DATA_SHARING = 'clinical-data-sharing',
  ADD_DEPENDENT = 'add-dependent',
  CHANGE_EMAIL = 'change-email',
  MFA = 'mfa',
  PHONE_NUMBER = 'phone-number',
  LOCATION_TIMEZONE = 'location-timezone',
  DISPLAY_LANGUAGE = 'display-language',
  NOTIFICATIONS = 'notifications',
  TRANSFER_ACCOUNT = 'transfer-account',
  ADD_HEALTH_PLAN = 'add-health-plan',
  ADD_PAYMENT_METHOD = 'add-payment-method',
  LATE_CANCELLATION_PAYMENT = 'late-cancellation-payment',
  FORGOT_PASSWORD = 'forgot-password',

  // External Links
  LYRA_APP = 'lyra-app',

  // Help
  FAQ = 'faq',
}

/**
 * Base interface for all searchable page metadata.
 */
interface BaseSearchablePageMetadata {
  /** Unique identifier for the searchable page */
  id: SEARCHABLE_PAGE_ID
  /** Display name shown in search results */
  displayName: string
  /** Keywords for fuzzy search matching */
  keywords: string[]
  /** Category for group-level ranking */
  category: SEARCH_PAGE_CATEGORY
  /** Priority within category (lower = higher rank). Used for tie-breaking. */
  priority?: number
  /**
   * When true, requires a stricter match (lower threshold) to appear in results.
   * Used for sensitive programs where vague searches should not surface the item.
   * Standard fuzzy search uses a higher threshold.
   */
  strictMatchOnly?: boolean
}

/**
 * Standard route that maps directly to a route path.
 * Web uses the path directly, mobile maps to screens via URL_PATH_TO_SCREEN.
 */
export interface StandardRouteMetadata extends BaseSearchablePageMetadata {
  type: SEARCHABLE_PAGE_TYPE.STANDARD
  /** Route path (e.g., '/secure/index/search-care') */
  route: string
}

/**
 * Route that requires additional parameters (e.g., routes with query params).
 */
export interface ParamRouteMetadata extends BaseSearchablePageMetadata {
  type: SEARCHABLE_PAGE_TYPE.PARAM_ROUTE
  /** Base route path */
  route: string
  /** Route parameters to append */
  params: Record<string, string | undefined>
}

/**
 * Direct Program Link route with CTPO (clientele, treatment, partner, offering) params.
 * Used for program-specific booking flows.
 */
export interface DirectProgramLinkMetadata extends BaseSearchablePageMetadata {
  type: SEARCHABLE_PAGE_TYPE.DIRECT_PROGRAM_LINK
  directLinkParams: DirectPathBookingApplinkParams
}

/**
 * External URL that opens in browser/new tab.
 * Used for oncehub links, microsites, etc.
 */
export interface ExternalUrlMetadata extends BaseSearchablePageMetadata {
  type: SEARCHABLE_PAGE_TYPE.EXTERNAL_URL
  url: string
}

/**
 * Union of all searchable page types.
 */
export type SearchablePageMetadata =
  | StandardRouteMetadata
  | ParamRouteMetadata
  | DirectProgramLinkMetadata
  | ExternalUrlMetadata

/**
 * Category priority for ranking search results.
 * Lower number = higher priority (shown first when scores are equal).
 */
export const CATEGORY_PRIORITY: Record<SEARCH_PAGE_CATEGORY, number> = {
  [SEARCH_PAGE_CATEGORY.TRIAGE]: 1,
  [SEARCH_PAGE_CATEGORY.PROGRAM]: 2,
  [SEARCH_PAGE_CATEGORY.CARE]: 3,
  [SEARCH_PAGE_CATEGORY.TOOLKIT]: 4,
  [SEARCH_PAGE_CATEGORY.LIBRARY]: 5,
  [SEARCH_PAGE_CATEGORY.ACCOUNT]: 6,
  [SEARCH_PAGE_CATEGORY.HELP]: 7,
  [SEARCH_PAGE_CATEGORY.EXTERNAL]: 8,
}

/**
 * Result from universal search, including the matched page and relevance score.
 */
export interface UniversalSearchResult {
  /** The matched searchable page metadata */
  item: SearchablePageMetadata
  /** Relevance score from Fuse.js (0 = perfect match, 1 = worst match) */
  score: number
}

/**
 * Options for the debounced universal search hook.
 */
export interface UseUniversalSearchDebouncedOptions {
  /** Search query string (raw input, will be debounced internally) */
  query: string
  /** Maximum number of results to return */
  limit?: number
  /** Debounce delay in milliseconds */
  debounceMs?: number
}

/**
 * Debug info about eligible pages for troubleshooting search issues.
 */
export interface EligiblePagesDebugInfo {
  totalCount: number
  /** Count of strict match pages */
  strictCount: number
  /** Count of standard match pages */
  standardCount: number
  pageNames: string[]
}

/**
 * Result from the debounced universal search hook.
 */
export interface UseUniversalSearchDebouncedResult {
  /** Search results based on the debounced query */
  results: UniversalSearchResult[]
  /** The current debounced query being searched */
  debouncedQuery: string
  /** Whether a search is pending (query changed but debounce hasn't fired yet) */
  isPending: boolean
  /** Debug info about eligible pages */
  eligiblePagesDebugInfo: EligiblePagesDebugInfo
}
